import _ from 'lodash';
import { useState } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { faQuestionCircle, faTimes } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon, Select } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { IconDefinition } from '@seeqdev/icons';

export interface OutputSelectorOption {
  value: string;
  label: string;
  icon?: IconDefinition;
  iconColor?: string;
  tooltip?: string;
}

export interface OperationalizeOutputSelectorIF extends ValidatingFormComponent<string[]> {
  component: 'OperationalizeOutputSelectorFormComponent';
  options: OutputSelectorOption[];
  value: string[];
  onChange: (selected: string[]) => void;
  groupLabel?: string;
  defaultValues?: string[];
}

const ADD_ALL_VALUE = '__add_all__';

const OUTPUT_CHIP_STYLE = {
  border: '1px solid var(--sq-dark-gray)',
  background: 'var(--sq-transparent-gray)',
  borderRadius: '4px',
  padding: '2px 6px',
};

export const OperationalizeOutputSelectorFormComponent = (props: OperationalizeOutputSelectorIF) => {
  const {
    name,
    testId = 'outputSelector',
    validation,
    extendValidation,
    customErrorText,
    customErrorParams,
    wrapperClassNames,
    value = [],
    options,
    groupLabel,
    defaultValues,
    required,
  } = props;

  const { t } = useTranslation();

  const defaultValidation = (val: string[] | undefined) => (required ? _.isEmpty(val) : false);
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const { errors, hasValidationErrors, modified, submitFailed } = useFormState({
    subscription: { errors: true, hasValidationErrors: true, modified: true, submitFailed: true },
  });

  const showError =
    _.has(errors, name) && hasValidationErrors && ((modified?.[name] as boolean | undefined) || submitFailed);

  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const selectedSet = new Set(value);
  const unselectedOptions = options.filter((opt) => !selectedSet.has(opt.value));
  const hasSelected = value.length > 0;
  const placeholder = hasSelected
    ? t('INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.ADD_MORE_ITEMS')
    : t('INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.ADD_ITEMS');

  function handleSelectItem(selected: { value: string } | null) {
    if (selected) {
      const newValue = [...value, selected.value];
      props.onChange(newValue);
    }
  }

  function handleRemoveItem(itemValue: string) {
    const newValue = value.filter((v) => v !== itemValue);
    props.onChange(newValue);
  }

  function handleAddAll() {
    const allValues = options.map((opt) => opt.value);
    props.onChange(allValues);
  }

  function handleClearAll() {
    props.onChange([]);
  }

  function handleAddDefaults() {
    if (defaultValues) {
      props.onChange(defaultValues);
    }
  }

  function getOptionLabel(option: OutputSelectorOption) {
    if (option.value === ADD_ALL_VALUE) {
      return (
        <FakeLink testId={`${testId}-addAll`} onClick={handleAddAll}>
          {option.label}
        </FakeLink>
      );
    }
    const sourceOption = options.find((o) => o.value === option.value);
    return (
      <div className="flexColumnContainer tw:items-center">
        {sourceOption?.icon && (
          <Icon
            icon={sourceOption.icon}
            type={sourceOption.iconColor ? 'color' : 'inherit'}
            color={sourceOption.iconColor}
            extraClassNames="fa-fw mr5"
          />
        )}
        <span>{option.label}</span>
        {sourceOption?.tooltip && (
          <Icon
            icon={faQuestionCircle}
            tooltip={t(sourceOption.tooltip)}
            tooltipPlacement="right"
            extraClassNames="ml5"
          />
        )}
      </div>
    );
  }

  function getOptionValue(option: OutputSelectorOption) {
    return option.value;
  }

  function renderAddAllOption() {
    if (unselectedOptions.length <= 1) return null;
    return {
      label: '',
      options: [
        {
          value: ADD_ALL_VALUE,
          label: t('DETAILS_SELECTION.ADD_ALL'),
        },
      ],
    };
  }

  function handleSelectChange(selected: readonly { value: string }[] | null) {
    if (!selected || selected.length === 0) return;
    const lastSelected = selected[selected.length - 1];
    if (lastSelected.value === ADD_ALL_VALUE) {
      handleAddAll();
    } else {
      handleSelectItem(lastSelected);
    }
  }

  function formatGroupLabel(group: { label?: string }) {
    if (!group.label) {
      return null;
    }
    return <span>{group.label}</span>;
  }

  function buildDropdownOptions() {
    const itemOptions = unselectedOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }));

    const addAllGroup = renderAddAllOption();
    if (addAllGroup) {
      return [{ label: '', options: itemOptions }, addAllGroup];
    }
    return itemOptions;
  }

  function renderChip(itemValue: string) {
    const option = options.find((o) => o.value === itemValue);
    if (!option) return null;

    return (
      <div
        key={itemValue}
        className="flexColumnContainer tw:mr-sq-3 tw:mb-sq-3 tw:items-center"
        style={OUTPUT_CHIP_STYLE}
        data-testid={`${testId}-chip-${itemValue}`}>
        {option.icon && (
          <Icon
            icon={option.icon}
            type={option.iconColor ? 'color' : 'inherit'}
            color={option.iconColor}
            extraClassNames="fa-fw mr3"
          />
        )}
        <span className="text-nowrap tw:mr-sq-3">{option.label}</span>
        {option.tooltip && (
          <Icon icon={faQuestionCircle} tooltip={t(option.tooltip)} tooltipPlacement="right" extraClassNames="mr3" />
        )}
        <Icon
          icon={faTimes}
          testId={`${testId}-remove-${itemValue}`}
          extraClassNames="cursorPointer"
          onClick={() => handleRemoveItem(itemValue)}
        />
      </div>
    );
  }

  return (
    <FormFieldWrapper
      testId={testId}
      wrapperClassNames={wrapperClassNames}
      showError={showError}
      customErrorText={customErrorText}
      customErrorParams={customErrorParams}>
      <Field<string[]> name={name} validate={appliedValidation}>
        {({ input }) => {
          return (
            <div data-testid={`${testId}-container`}>
              {groupLabel && (
                <div className="flexColumnContainer tw:mb-sq-5 tw:w-full tw:items-center tw:justify-between">
                  <strong>{groupLabel}</strong>
                  {hasSelected ? (
                    <FakeLink
                      testId={`${testId}-clearAll`}
                      extraClassNames="ml15 tw:italic"
                      onClick={() => {
                        handleClearAll();
                        input.onChange([]);
                      }}>
                      {t('INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.CLEAR_ALL')}
                    </FakeLink>
                  ) : (
                    defaultValues && (
                      <FakeLink
                        testId={`${testId}-addDefaults`}
                        extraClassNames="ml15 tw:italic"
                        onClick={() => {
                          handleAddDefaults();
                          input.onChange(defaultValues);
                        }}>
                        {t('INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.ADD_DEFAULTS')}
                      </FakeLink>
                    )
                  )}
                </div>
              )}
              {hasSelected && (
                <div className="flexColumnContainer tw:mb-sq-5 tw:flex-wrap" data-testid={`${testId}-chips`}>
                  {value.map(renderChip)}
                </div>
              )}
              <div onMouseLeave={() => setMenuIsOpen(false)}>
                <Select
                  placeholder={placeholder}
                  value={[]}
                  options={buildDropdownOptions()}
                  getOptionValue={getOptionValue}
                  getOptionLabel={getOptionLabel}
                  formatGroupLabel={formatGroupLabel}
                  onChange={(selected) => {
                    handleSelectChange(selected as readonly { value: string }[] | null);
                    input.onChange(value);
                  }}
                  isMulti
                  closeMenuOnSelect={false}
                  menuIsOpen={menuIsOpen}
                  onMenuOpen={() => setMenuIsOpen(true)}
                  onMenuClose={() => setMenuIsOpen(false)}
                  isSearchable={false}
                  isDisabled={unselectedOptions.length === 0}
                  controlClassNames="cursorPointer"
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
